import CiroTable from "../shared/CiroTable/CiroTable";
import CiroTableHeader, {
  CiroTableHeaderAlignEnum,
} from "../shared/CiroTable/CiroTableHeader";
import classNames from "classnames";
import CiroTableRow from "../shared/CiroTable/CiroTableRow";
import CiroTableCell, {
  CiroTableCellAlignEnum,
} from "../shared/CiroTable/CiroTableCell";
import CiroButton, { CiroButtonStyleEnum } from "../shared/CiroButton";
import { PaperAirplaneIcon, XCircleIcon } from "@heroicons/react/24/outline";
import CiroNoRecordsCard from "../shared/CiroNoRecordsCard";
import SkeletonLoading from "../shared/SkeletonLoading";
import { gql, useMutation, useQuery } from "@apollo/client";
import toast from "react-hot-toast";
import CiroConfirmationModal from "../shared/CiroConfirmationModal";
import { useCallback, useState } from "react";
import {
  Team_CancelInviteMutation,
  Team_CancelInviteMutationVariables,
  Team_OrganizationUserAccountFragment,
  Team_ResendInviteEmailMutation,
  Team_ResendInviteEmailMutationVariables,
  Team_UserInvitePermissionsFragment,
  Team_UserInviteQuery,
  Team_UserInviteQueryVariables,
  UserInviteStatus,
} from "../../__generated__/graphql";

export const Team_OrganizationUserAccount = gql`
  fragment Team_OrganizationUserAccount on UserAccount {
    pk
    email
    first
    last
    created_at
  }
`;

export const Team_UserInvitePermissions = gql`
  fragment Team_UserInvitePermissions on Permissions {
      hasTeamManagement
  }
  `;

export const Team_UserInvite = gql`
  query Team_UserInvite($input: UserInvitesInput!) {
    userInvites(input: $input) {
      id
      email
    }
  }
`;
const Team_CancelInvite = gql`
  mutation Team_CancelInvite($input: CancelInviteInput!) {
    cancelInvite(input: $input) {
      success
      error
    }
  }
`;

const Team_ResendInviteEmail = gql`
  mutation Team_ResendInviteEmail($input: ResendInviteEmailInput!) {
    resendInviteEmail(input: $input) {
      success
      error
    }
  }
`;

interface ITeamMembersTable {
  userAccountsData?: readonly Team_OrganizationUserAccountFragment[] | null;
  userAccountLoading?: boolean;
  permissions?: Team_UserInvitePermissionsFragment | null;
}

export const TeamMembersTable = ({
  userAccountsData,
  userAccountLoading = false,
  permissions,
}: ITeamMembersTable) => {
  const [cancelInviteId, setCancelInviteId] = useState<number | null>(null);
  const { data: userInviteData, loading: userInviteLoading } = useQuery<
    Team_UserInviteQuery,
    Team_UserInviteQueryVariables
  >(Team_UserInvite, {
    variables: { input: { status: UserInviteStatus.Pending } },
    skip: !permissions?.hasTeamManagement,
  });

  const userInvites =
    userInviteData?.userInvites.map((invite) => ({
      ...invite,
      userInviteId: invite.id,
      pendingInvite: true,
      pk: invite.id,
      first: null,
      last: null,
      created_at: null,
    })) ?? [];

  const userAccounts =
    userAccountsData?.map((account) => ({
      ...account,
      pendingInvite: false,
      userInviteId: null,
    })) ?? [];

  const teamMembers = [...userAccounts, ...userInvites];
  const isPageLoading = userInviteLoading || userAccountLoading;

  const [cancelInvite] = useMutation<
    Team_CancelInviteMutation,
    Team_CancelInviteMutationVariables
  >(Team_CancelInvite, {
    onError: (error) => {
      toast.error("Error cancelling invite");
      console.error("Error cancelling invite:", error);
    },
    onCompleted: (data) => {
      if (!data.cancelInvite.success) {
        toast.error("Error cancelling invite");
        console.error("Error cancelling invite:", data.cancelInvite.error);
      } else {
        toast.success("Invite cancelled successfully");
      }
    },
  });

  const handleCancelInvite = useCallback(() => {
    if (!cancelInviteId) return;
    cancelInvite({
      variables: { input: { id: cancelInviteId } },
      refetchQueries: [
        {
          query: Team_UserInvite,
          variables: { input: { status: UserInviteStatus.Pending } },
        },
      ],
    });
    setCancelInviteId(null);
  }, [cancelInviteId, cancelInvite]);

  const [resendInvite] = useMutation<
    Team_ResendInviteEmailMutation,
    Team_ResendInviteEmailMutationVariables
  >(Team_ResendInviteEmail, {
    onError: (error) => {
      toast.error("Error resending invite");
      console.error("Error resending invite:", error);
    },
    onCompleted: (data) => {
      if (!data.resendInviteEmail.success) {
        toast.error("Error resending invite");
        console.error("Error resending invite:", data.resendInviteEmail.error);
      } else {
        toast.success("Invite resent successfully");
      }
    },
  });

  if (teamMembers.length === 0) {
    if (isPageLoading) {
      return <SkeletonLoading numSkeletons={25} skeletonHeight={"3rem"} />;
    }

    return (
      <CiroNoRecordsCard
        title="No team members found"
        subtitle="Please refresh the page or contact support."
      />
    );
  }

  const headers = [
    <CiroTableHeader key={1} width={"w-64"} isFirst={true}>
      Name
    </CiroTableHeader>,
    <CiroTableHeader
      key={2}
      width="w-64"
      className={classNames("hidden", "lg:table-cell")}
    >
      Email
    </CiroTableHeader>,
    <CiroTableHeader
      key={3}
      width="w-64"
      className={classNames("hidden", "sm:table-cell")}
    >
      Joined
    </CiroTableHeader>,
    <CiroTableHeader
      key={4}
      width={classNames("hidden", "w-40", "sm:table-cell")}
    >
      Role
    </CiroTableHeader>,
    <CiroTableHeader
      key={5}
      isLast={true}
      width={classNames("hidden", "w-40", "sm:table-cell")}
      align={CiroTableHeaderAlignEnum.CENTER}
    >
      Actions
    </CiroTableHeader>,
  ].filter(Boolean);

  return (
    <div className={classNames("w-full", "test-business-table")}>
      <CiroConfirmationModal
        text="Are you sure you want to cancel this invite?"
        isOpen={!!cancelInviteId}
        onClose={() => setCancelInviteId(null)}
        onConfirm={handleCancelInvite}
        analyticsField="Confirm cancel user invite modal"
      />
      <CiroTable>
        <thead className={classNames("table-header-group")}>
          <CiroTableRow clickable={false} sticky={true}>
            {headers}
          </CiroTableRow>
        </thead>
        <tbody className={classNames("table-row-group")}>
          {teamMembers.map(function (teamMember) {
            const {
              userInviteId,
              first,
              last,
              email,
              created_at,
              pendingInvite,
            } = teamMember ?? {};
            const joinedDate = created_at
              ? new Date(created_at).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "2-digit",
                })
              : "";

            const userName =
              first || last ? `${first ?? ""} ${last ?? ""}` : "Ciro User";
            return (
              <CiroTableRow
                key={userInviteId || teamMember.pk}
                clickable={false}
              >
                <CiroTableCell>
                  <div>
                    {pendingInvite ? (
                      <span className={classNames("text-neutral-500 italic")}>
                        Invite Pending
                      </span>
                    ) : (
                      userName
                    )}
                  </div>
                </CiroTableCell>
                <CiroTableCell>
                  <div>{email}</div>
                </CiroTableCell>
                <CiroTableCell>
                  <div>{joinedDate}</div>
                </CiroTableCell>
                <CiroTableCell>
                  <div>Admin</div> {/* Update once we have user roles */}
                </CiroTableCell>
                <CiroTableCell
                  padding={2}
                  align={CiroTableCellAlignEnum.CENTER}
                >
                  {/* Update once we have user removals */}
                  {pendingInvite && userInviteId && (
                    <div
                      className={classNames("flex", "gap-2", "justify-center")}
                    >
                      <CiroButton
                        analyticsField="Resend invite"
                        style={CiroButtonStyleEnum.UNSTYLED}
                        tooltip="Resend Invite"
                        onClick={() => {
                          resendInvite({
                            variables: { input: { userInviteId } },
                          });
                        }}
                      >
                        <PaperAirplaneIcon className={classNames("h-6")} />
                      </CiroButton>
                      <CiroButton
                        analyticsField="Cancel invite"
                        style={CiroButtonStyleEnum.UNSTYLED}
                        tooltip="Cancel Invite"
                        onClick={() => {
                          setCancelInviteId(userInviteId);
                        }}
                      >
                        <XCircleIcon className={classNames("h-6")} />
                      </CiroButton>
                    </div>
                  )}
                </CiroTableCell>
              </CiroTableRow>
            );
          })}
        </tbody>
      </CiroTable>
    </div>
  );
};
